import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  Paper,
  Divider,
  Avatar,
  Button,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import LogoutIcon from "@mui/icons-material/Logout";
import StarsIcon from "@mui/icons-material/Stars";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { EXCHANGE_CODES } from "../../websocket/constants";
import octopusInstance from "../../services/octopus/octopusInstance";
import { getPinnedInstrumentsAction, setPinnedInstrumentsAction } from "../../store/actions/watchlist";
import { useSnackbar } from "notistack";
import { API_CONFIG, getApiUrl } from "../../config/api";
import { getDeviceId } from "../../utils/deviceId";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import { useDispatch, useSelector } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import useMediaQuery from "@mui/material/useMediaQuery";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterListIcon from '@mui/icons-material/FilterList';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import useBalance from "../Portfolio/Positions/useBalance";
import { fetchFundsView } from "../../store/actions/funds";
import { updateCalculatedFunds } from "../../store/actionCreators/fundsActions";
import WithdrawModal from "../Profile/WithdrawModal";
import AddFundsModal from "../Common/AddFundsModal";
import PaymentGuidelinesModal from "../Common/PaymentGuidelinesModal";
import {
  getFundsChipContainerStyle,
  getFundsChipIconStyle,
  getFundsChipTextStyle,
} from "./AppBarCss";
import ProfileMenuIcon from "./ProfileMenuIcon";
import FundsMenuOverlay from "../Common/FundsMenuOverlay";
import ThemeVariantSelector from "../Profile/components/ThemeVariantSelector";
import ExchangeTag from "../Common/ExchangeTag";


// Try to import the logo
// let tradehLogo;
// try {
//   tradehLogo = require("../../images/tradeh-logo.png");
// } catch (e) {
//   console.warn("Logo file not found in src/images/tradeh-logo.png");
// }

import brokerConfig from "../../config/brokerConfig";

import PinnedInstrumentsList from "./PinnedInstrumentsList";

const AppBar = ({
  onNavigate,
  toggleTheme,
  onLogout,
  position,
  handleChartOpen,
  activeComponent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const pinnedInstruments = useSelector((state) => state.marketwatch.pins);
  const { userData, brokerSettings } = useSelector((state) => state.profile);
  const isReferralActive = brokerSettings?.referral_dashboard === "true";
  const isBseBfoEnabled = String(brokerSettings?.bsebfoenabled).toLowerCase() === "true";
  const isPriceAlertsEnabled = String(brokerSettings?.pricealertsenabled).toLowerCase() === "true";
  const [isVisible, setIsVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const dispatch = useDispatch();
  const [dynamicInfo, setDynamicInfo] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Funds chip + popover state
  const balanceData = useBalance();
  const [fundsPopoverAnchor, setFundsPopoverAnchor] = useState(null);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false);
  const [isPaymentGuidelinesModalOpen, setIsPaymentGuidelinesModalOpen] = useState(false);
  const [mobileFundsOpen, setMobileFundsOpen] = useState(false);
  const payInComponentRef = useRef();

  const handleFundsRefresh = useCallback(() => {
    dispatch(fetchFundsView());
    dispatch(updateCalculatedFunds());
  }, [dispatch]);

  async function callHandleAddFundsFromPayIn() {
    await payInComponentRef.current.handleAddFunds();
  }

  const { enqueueSnackbar } = useSnackbar();
  const [anchorElPins, setAnchorElPins] = useState(null);
  const [isSearchPinOpen, setIsSearchPinOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handlePinsClick = (event) => {
    setAnchorElPins(event.currentTarget);
  };

  const handlePinsClose = () => {
    setAnchorElPins(null);
  };

  const handleSearchPinClose = () => {
    setIsSearchPinOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleUnpin = (slotIndex, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (slotIndex === 0 || slotIndex === 1) {
      enqueueSnackbar("The first two pinned instruments cannot be unpinned.", { variant: "warning" });
      return;
    }
    const removedInstrument = pinnedInstruments[slotIndex];
    const newPins = pinnedInstruments.filter((_, idx) => idx !== slotIndex);

    dispatch(setPinnedInstrumentsAction(newPins))
      .then(() => {
        enqueueSnackbar(
          `Unpinned ${removedInstrument?.display_name || removedInstrument?.trading_symbol || "instrument"}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to unpin instrument", { variant: "error" });
      });
  };

  const handlePin = (instrument, event) => {
    if (event) {
      event.stopPropagation();
    }
    const newPins = [...pinnedInstruments, {
      display_name: instrument.display_name || instrument.trading_symbol || "",
      exchange: instrument.exchange || "",
      is_index: instrument.is_index ?? false,
      is_tradable: instrument.is_tradable ?? true,
      token: instrument.token,
      trading_symbol: instrument.trading_symbol || "",
    }];

    dispatch(setPinnedInstrumentsAction(newPins))
      .then(() => {
        enqueueSnackbar(
          `Pinned ${instrument.display_name || instrument.trading_symbol}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to pin instrument", { variant: "error" });
      });
  };

  const handleMovePin = (sourceIndex, targetIndex, event) => {
    if (event) {
      event.stopPropagation();
    }
    const newPins = [...pinnedInstruments];
    const temp = newPins[targetIndex];
    newPins[targetIndex] = newPins[sourceIndex];
    newPins[sourceIndex] = temp;

    dispatch(setPinnedInstrumentsAction(newPins))
      .then(() => {
        enqueueSnackbar(
          `Moved ${newPins[targetIndex]?.display_name || newPins[targetIndex]?.trading_symbol || "instrument"} to Slot ${targetIndex + 1}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to move instrument", { variant: "error" });
      });
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);

    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${getApiUrl(API_CONFIG.ENDPOINTS.SEARCH_INSTRUMENTS)}?key=${encodeURIComponent(value)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization-token': token,
          'x-device-type': 'WEB',
          'x-device-id': getDeviceId()
        }
      });

      const data = await response.json();
      if (!data.error || data.error.code === 0) {
        const excludedExchanges = isBseBfoEnabled ? [] : ['BSE', 'BFO'];
        const rawResults = data.result || [];
        const filteredResults = rawResults.filter(
          (instrument) => instrument?.exchange && !excludedExchanges.includes(instrument.exchange.toUpperCase())
        );
        setSearchResults(filteredResults);
      } else {
        console.error('Search failed:', data.error.message);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
      if (document.visibilityState === "visible") {
        // Refresh data when returning to tab
        dispatch(getPinnedInstrumentsAction());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Fetch initial pinned instruments
  useEffect(() => {
    dispatch(getPinnedInstrumentsAction());
  }, [dispatch]);

  useEffect(() => {
    if (!isVisible) return; // Don't subscribe when tab is hidden
    const handlers = [];
    pinnedInstruments.forEach((index) => {
      if (!index) return;
      const tokenVal = index.token || index.instrumentToken || index.instrument_token;
      if (!tokenVal) return;

      let exchangeCode = 1;
      if (index.exchange) {
        const exchUpper = index.exchange.toUpperCase();
        if (EXCHANGE_CODES[exchUpper]) {
          exchangeCode = EXCHANGE_CODES[exchUpper];
        } else {
          exchangeCode = index.exchangeCode || index.exchange_code || 1;
        }
      } else {
        exchangeCode = index.exchangeCode || index.exchange_code || 1;
      }

      const subscriptionLocation = `pinnedInstrument-${tokenVal}`;
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation,
        payload: {
          exchangeCode,
          instrumentToken: tokenVal,
        },
      });

      if (handler) {
        handler.subscribe(({ msg }) => {
          setDynamicInfo((prev) => ({
            ...prev,
            [tokenVal]: {
              ...msg,
              selectedClass:
                prev[tokenVal]?.ltp < msg.ltp
                  ? "buy"
                  : prev[tokenVal]?.ltp > msg.ltp
                    ? "sell"
                    : "",
            },
          }));
        });
      }

      handlers.push(handler);
    });
    return () => {
      handlers.forEach((handler) => handler && handler.unsubscribe());
    };
  }, [pinnedInstruments]);

  const isScreenersEnabled = useSelector((state) => state.profile.brokerSettings?.screeners) === "true";

  const menuItems = [
    {
      label: "Profile",
      action: () => {
        onNavigate("profile");
        setAnchorEl(null);
      },
      icon: <AccountCircleIcon fontSize="small" />,
    },
    ...(isReferralActive ? [{
      label: "Referral",
      action: () => {
        onNavigate("referral");
        setAnchorEl(null);
      },
      icon: <StarsIcon fontSize="small" />,
    }] : []),
    ...(isScreenersEnabled ? [{
      label: "Screeners",
      action: () => {
        onNavigate("screeners");
        setAnchorEl(null);
      },
      icon: <FilterListIcon fontSize="small" />,
    }] : []),
    ...(isPriceAlertsEnabled ? [{
      label: "Price Alerts",
      action: () => {
        onNavigate("price-alerts");
        setAnchorEl(null);
      },
      icon: <NotificationsActiveOutlinedIcon fontSize="small" />,
    }] : []),
    {
      label: "Reports",
      action: () => {
        onNavigate("tradehistory");
        setAnchorEl(null);
      },
      icon: <AssessmentIcon fontSize="small" />,
    },
    ...(!brokerConfig.hideContact ? [{
      label: "Support",
      action: () => {
        onNavigate("support");
        setAnchorEl(null);
      },
      icon: <SupportAgentIcon fontSize="small" />,
    }] : []),
    {
      label: "Logout",
      action: () => {
        // Use the centralized logout logic from App.js
        onLogout();
        setAnchorEl(null);
      },
      icon: <LogoutIcon fontSize="small" />,
    }
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const isItemActive = (label) => {
    if (!label) return false;
    const normalizedLabel = label.toLowerCase().replace(/\s+/g, "-");
    if (normalizedLabel === "reports" && activeComponent === "tradehistory") {
      return true;
    }
    return activeComponent === normalizedLabel;
  };

  const navigationItems = [
    {
      label: "Dashboard",
      icon: <DashboardIcon />,
      action: () => onNavigate("dashboard")
    },
    {
      label: "Orders",
      icon: <ReceiptIcon />,
      action: () => onNavigate("orders")
    },
    {
      label: "Portfolio",
      icon: <AccountBalanceWalletIcon />,
      action: () => onNavigate("portfolio")
    },
    {
      label: "Funds",
      icon: <PaymentsIcon />,
      action: () => onNavigate("funds")
    }
  ];

  const drawerContent = (
    <Box
      sx={{
        width: 250,
        height: "100%",
        background: "linear-gradient(135deg, #0A1929 0%, #0F2942 100%)",
        color: "white",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "auto",
        "&::before": {
          content: '""',
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(79, 172, 254, 0.1) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(0, 242, 254, 0.08) 0%, transparent 40%)",
          pointerEvents: "none",
          zIndex: 0,
        }
      }}
    >
      {/* Logo in drawer header */}
      <Box sx={{
        height: "5rem",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        marginTop: "1rem"
      }}>
        <img
          src={brokerConfig.logoFull}
          alt={brokerConfig.title}
          style={{
            width: brokerConfig.logoDimensions?.appBar?.width || 140,
            height: brokerConfig.logoDimensions?.appBar?.height || 45,
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* User Profile Header */}
      <Box
        sx={{
          pt: 2,
          pb: 1,
          px: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Avatar */}
        <Avatar
          sx={{
            width: "3.5rem",
            height: "3.5rem",
            background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            color: "white",
            fontSize: "1.5rem",
            border: "0.15rem solid rgba(255, 255, 255, 0.2)",
            boxShadow: "0 0.25rem 1rem rgba(0,0,0,0.2)",
            mb: 1.5,
          }}
        >
          {userData?.name?.charAt(0).toUpperCase() || "U"}
        </Avatar>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "white",
            mb: 0.5,
          }}
        >
          {userData?.name || "User"}
        </Typography>


        <Box
          sx={{
            display: "inline-block",
            px: 1.25,
            py: 0.25,
            borderRadius: "0.5rem",
            backgroundColor: "rgba(79, 172, 254, 0.15)",
            border: "0.0625rem solid rgba(79, 172, 254, 0.3)",
            mt: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#4facfe",
              fontWeight: 600,
              letterSpacing: 0.5,
              fontSize: "0.65rem",
            }}
          >
            Active
          </Typography>
        </Box>
      </Box>



      {/* Account Actions */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255, 255, 255, 0.5)",
            textTransform: "uppercase",
            fontWeight: 600,
            letterSpacing: 0.5,
            px: 2,
            pt: 1.5,
            pb: 0.5,
            display: "block",
          }}
        >
          Appearance
        </Typography>
        <List sx={{ px: 2, "& .MuiListItem-root": { mb: 0.25, borderRadius: "0.75rem" } }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.label}>
              {item.sectionLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    px: 2,
                    pt: 1.5,
                    pb: 0.5,
                    display: "block",
                  }}
                >
                  {item.sectionLabel}
                </Typography>
              )}
              <ListItem
                button
                onClick={() => {
                  item.action();
                  setDrawerOpen(false);
                }}
                sx={{
                  color: "white",
                  py: 0.75,
                  px: 2,
                  backgroundColor: isItemActive(item.label)
                    ? "rgba(79, 172, 254, 0.15)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                  },
                  position: "relative",
                  "&::after": isItemActive(item.label) ? {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "20%",
                    height: "60%",
                    width: "0.1875rem",
                    backgroundColor: "#4facfe",
                    borderRadius: "0.25rem",
                  } : {},
                }}
              >
                <ListItemIcon sx={{
                  color: item.label === 'Logout' 
                    ? '#ff4d4f' 
                    : (isItemActive(item.label) ? "#4facfe" : "white"),
                  minWidth: "2.5rem"
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    fontWeight: item.label === 'Logout' ? 600 : (isItemActive(item.label) ? 600 : 500),
                    color: item.label === 'Logout' ? '#ff4d4f' : 'inherit'
                  }}
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Box>



      {/* Footer */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          mt: "auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "rgba(255, 255, 255, 0.5)",
            fontWeight: 400,
          }}
        >
          TradeH Trading Platform{" "}
          <Box
            component="span"
            sx={{
              color: "#4facfe",
              fontWeight: 600,
            }}
          >
            • v1.0.0
          </Box>
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <MuiAppBar
        position="fixed"
        sx={{
          background: "linear-gradient(90deg, #0A1929 0%, #132f4c 100%)",
          color: "white",
          boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.15)",
          height: isMobile ? "3.5rem" : "4rem",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            gap: isMobile ? 1 : 0,
            p: isMobile ? "0 0.75rem" : "0 1.5rem",
            minHeight: isMobile ? "3.5rem !important" : "4rem !important",
            height: "100%",
            justifyContent: "space-between",
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, padding: 0 }}
            >
              <ProfileMenuIcon userName={userData?.name} />
            </IconButton>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              overflow: "hidden",
            }}
          >
            {!isMobile && brokerConfig.logoSmall && (
              <img
                src={brokerConfig.logoSmall}
                alt={brokerConfig.title}
                style={{
                  height: "2.5rem",
                  width: "auto",
                  marginRight: "1.5rem",
                  objectFit: 'contain'
                }}
              />
            )}
            <PinnedInstrumentsList
              isMobile={isMobile}
              pinnedInstruments={pinnedInstruments}
              dynamicInfo={dynamicInfo}
            />
            <IconButton
              onClick={handlePinsClick}
              sx={{
                color: "white",
                ml: 0.25,
                mr: 1.5,
                p: 0.25,
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
              size="small"
            >
              <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem" }} />
            </IconButton>

            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  whiteSpace: "nowrap",
                  marginLeft: "auto",
                  marginRight: "3rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "0.5rem",
                  padding: "0.25rem",
                  boxShadow: "0 0.125rem 0.5rem rgba(0,0,0,0.1)",
                  minWidth: "fit-content",
                }}
              >
                {navigationItems.map((item) => {
                  const isActive = isItemActive(item.label);
                  return (
                    <Button
                      key={item.label}
                      onClick={item.action}
                      startIcon={item.icon}
                      disableRipple
                      variant={isActive ? "contained" : "text"}
                      sx={{
                        mx: 0.25,
                        height: "2.25rem",
                        display: "inline-flex",
                        alignItems: "center",
                        textTransform: "none",
                        color: "#ffffff",
                        backgroundColor: isActive ? "rgba(79, 172, 254, 0.3)" : "transparent",
                        "&:hover": {
                          backgroundColor: isActive
                            ? "rgba(79, 172, 254, 0.4)"
                            : "rgba(255, 255, 255, 0.1)",
                        },
                        borderRadius: "0.5rem",
                        px: 1.5,
                        py: 0,
                        fontSize: "0.875rem",
                        lineHeight: 1,
                        fontWeight: isActive ? 600 : 400,
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        transition: "background-color 0.2s ease",
                        // Normalize icon size + spacing so icon and label sit centered.
                        "& .MuiButton-startIcon": {
                          marginRight: "0.375rem",
                          marginLeft: 0,
                        },
                        "& .MuiButton-startIcon > svg, & .MuiSvgIcon-root": {
                          fontSize: "1.15rem",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>

            <Box
              onClick={(e) => {
                if (isMobile) {
                  setMobileFundsOpen(true);
                } else {
                  setFundsPopoverAnchor(e.currentTarget);
                }
              }}
              sx={getFundsChipContainerStyle(isMobile)}
            >
              <AccountBalanceWalletIcon sx={getFundsChipIconStyle(isMobile)} />
              {balanceData.isLoading ? (
                <Skeleton variant="text" width={40} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
              ) : (
                <Typography
                  variant="body2"
                  sx={getFundsChipTextStyle(isMobile)}
                >
                  {balanceData.equityPercentage === '--' ? balanceData.formatted.availableCash : balanceData.formatted.equityPercentage}
                </Typography>
              )}
            </Box>

            <Tooltip title="Notifications">
              <IconButton
                onClick={() => onNavigate("notifications")}
                sx={{
                  color: "white",
                  ml: isMobile ? 1 : 1.5,
                  p: 0.5,
                  backgroundColor: activeComponent === "notifications"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <NotificationsIcon sx={{ fontSize: "1.3rem" }} />
              </IconButton>
            </Tooltip>



            {/* Profile Avatar - Desktop Only (Mobile uses left hamburger menu) */}
            {!isMobile && (
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{
                  padding: 0,
                  ml: 2,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ProfileMenuIcon userName={userData?.name} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </MuiAppBar>

      {!isMobile && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            elevation: 4,
            sx: {
              minWidth: "13.75rem",
              mt: 1,
              borderRadius: "0.75rem",
              background: "linear-gradient(135deg, #0A1929 0%, #0F2942 100%)",
              overflow: "hidden",
              border: "0.0625rem solid rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Box sx={{
            p: 2,
            borderBottom: "0.0625rem solid rgba(255, 255, 255, 0.1)",
            background: "rgba(79, 172, 254, 0.1)",
          }}>
            <Typography variant="subtitle1" sx={{ color: "white", fontWeight: 600 }}>
              {userData?.name || "User"}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              {userData?.email || ""}
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.7)" }}>
              {userData?.client_id || ""}
            </Typography>
          </Box>

          {menuItems.map((item) => (
            <React.Fragment key={item.label}>
              {item.sectionLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    px: 2,
                    pt: 1,
                    pb: 0.5,
                    display: "block",
                  }}
                >
                  {item.sectionLabel}
                </Typography>
              )}
              <MenuItem
                onClick={item.action}
                sx={{
                  color: "white",
                  py: 1.5,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: item.label === 'Logout' ? '#ff4d4f' : "rgba(255, 255, 255, 0.7)",
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: item.label === 'Logout' ? 600 : (isItemActive(item.label) ? 600 : 400),
                    color: item.label === 'Logout' ? '#ff4d4f' : 'inherit'
                  }}
                />
              </MenuItem>
            </React.Fragment>
          ))}


        </Menu>
      )}

      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "15.625rem",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <FundsMenuOverlay
        isMobile={isMobile}
        anchorEl={fundsPopoverAnchor}
        mobileOpen={mobileFundsOpen}
        onClose={() => {
          setFundsPopoverAnchor(null);
          setMobileFundsOpen(false);
        }}
        balanceData={balanceData}
        onWithdrawClick={() => setIsWithdrawModalOpen(true)}
        onAddFundsClick={() => setIsAddFundsModalOpen(true)}
        onRefreshClick={handleFundsRefresh}
      />

      {/* Withdraw Modal */}
      <WithdrawModal open={isWithdrawModalOpen} onClose={() => setIsWithdrawModalOpen(false)} />

      {/* Add Funds Modal */}
      <AddFundsModal
        open={isAddFundsModalOpen}
        onClose={() => setIsAddFundsModalOpen(false)}
        ref={payInComponentRef}
        setIsPaymentGuidelinesModalOpen={setIsPaymentGuidelinesModalOpen}
      />

      {/* Payment Guidelines Modal */}
      <PaymentGuidelinesModal
        open={isPaymentGuidelinesModalOpen}
        onClose={() => setIsPaymentGuidelinesModalOpen(false)}
        onProceed={() => callHandleAddFundsFromPayIn()}
      />

      <Popover
        anchorEl={anchorElPins}
        open={Boolean(anchorElPins)}
        onClose={handlePinsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: (theme) => ({
            width: 320,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0A1929 0%, #0F2942 100%)'
              : 'linear-gradient(135deg, #F0F4F8 0%, #FFFFFF 100%)',
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '0.75rem',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0,0,0,0.5)'
              : '0 8px 32px rgba(0,0,0,0.1)',
            p: 2,
            mt: 1
          })
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Pinned Instruments
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              handlePinsClose();
              setIsSearchPinOpen(true);
            }}
            sx={(theme) => ({
              textTransform: 'none',
              borderRadius: '20px',
              fontSize: '0.8rem',
              py: 0.5,
              px: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0, 255, 209, 0.2)' : 'rgba(33, 150, 243, 0.4)'}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              }
            })}
          >
            Add
          </Button>
        </Box>
        <Divider sx={{ mb: 1.5 }} />

        {/* Pinned List */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {pinnedInstruments.length === 0 || pinnedInstruments.every(p => !p) ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
              No instruments pinned. Click Add to pin.
            </Typography>
          ) : (
            pinnedInstruments.map((index, slotIndex) => {
              if (!index) return null;
              const tokenVal = index.token || index.instrumentToken || index.instrument_token;
              const displayName = index.display_name || index.trading_symbol || "";
              const data = dynamicInfo[tokenVal] || {};
              const ltp = parseFloat(data.ltp);
              const absoluteChange = parseFloat(data.absoluteChange);
              const percentChange = parseFloat(data.percentChange);
              const isPositive = percentChange >= 0;

              return (
                <Paper
                  key={tokenVal || displayName || slotIndex}
                  elevation={0}
                  sx={(theme) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 1.5,
                    borderRadius: '8px',
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                  })}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {displayName.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        Slot {slotIndex + 1}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                      {!isNaN(ltp) ? ltp.toFixed(2) : "-"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          color: isPositive ? 'success.main' : 'error.main',
                        }}
                      >
                        {!isNaN(absoluteChange) ? (isPositive ? `+${absoluteChange.toFixed(2)}` : absoluteChange.toFixed(2)) : "-"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isPositive ? 'success.main' : 'error.main',
                          fontSize: '0.7rem'
                        }}
                      >
                        {!isNaN(percentChange) ? `(${percentChange.toFixed(2)}%)` : "-"}
                      </Typography>
                    </Box>

                    {slotIndex >= 2 && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Move to Slot 1">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleMovePin(slotIndex, 0, e)}
                            sx={(theme) => ({
                              minWidth: '24px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              padding: 0,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                              color: theme.palette.text.primary,
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 255, 209, 0.2)' : 'rgba(33, 150, 243, 0.2)', // Using standard fallback or alpha if available
                              }
                            })}
                          >
                            1
                          </Button>
                        </Tooltip>
                        <Tooltip title="Move to Slot 2">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleMovePin(slotIndex, 1, e)}
                            sx={(theme) => ({
                              minWidth: '24px',
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              padding: 0,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                              color: theme.palette.text.primary,
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 255, 209, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                              }
                            })}
                          >
                            2
                          </Button>
                        </Tooltip>
                      </Box>
                    )}

                    <IconButton
                      size="small"
                      color="error"
                      disabled={slotIndex === 0 || slotIndex === 1}
                      onClick={(e) => handleUnpin(slotIndex, e)}
                      sx={{
                        backgroundColor: (slotIndex === 0 || slotIndex === 1) ? 'transparent' : 'rgba(244, 67, 54, 0.08)',
                        '&:hover': {
                          backgroundColor: (slotIndex === 0 || slotIndex === 1) ? 'transparent' : 'rgba(244, 67, 54, 0.2)',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>
      </Popover>

      <Dialog
        open={isSearchPinOpen}
        onClose={handleSearchPinClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: (theme) => ({
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #0A1929 0%, #0F2942 100%)'
              : 'linear-gradient(135deg, #F0F4F8 0%, #FFFFFF 100%)',
            color: theme.palette.text.primary,
            borderRadius: '1rem',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 12px 40px rgba(0,0,0,0.6)'
              : '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          })
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            pt: 2.5
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PushPinIcon fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Pin Instrument</Typography>
          </Box>
          <IconButton onClick={handleSearchPinClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, position: 'relative' }}>
            <TextField
              placeholder="Search symbols to pin..."
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                sx: { borderRadius: '8px' }
              }}
            />
            {searching && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: 'calc(50% - 10px)'
                }}
              />
            )}
          </Box>

          <List sx={{ maxHeight: 300, overflow: 'auto', p: 0 }}>
            {searchResults.length === 0 && searchQuery.length >= 2 && !searching ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                No results found
              </Typography>
            ) : (
              searchResults.map((result) => {
                // Check if already pinned
                const pinIndex = pinnedInstruments.findIndex(
                  (pin) =>
                    pin &&
                    String(pin.token) === String(result.token) &&
                    String(pin.exchange).toUpperCase() === String(result.exchange).toUpperCase()
                );
                const isPinned = pinIndex !== -1;
                const isLocked = pinIndex === 0 || pinIndex === 1;

                return (
                  <ListItem
                    key={result.token}
                    sx={(theme) => ({
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      py: 1.5,
                      px: 1,
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    })}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {result.display_name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <ExchangeTag exchange={result.exchange} />
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            • {result.trading_symbol}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isPinned ? (
                        <Tooltip title={isLocked ? "Pinned (Locked)" : "Unpin Instrument"}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={isLocked}
                              onClick={(e) => handleUnpin(pinIndex, e)}
                              sx={{
                                backgroundColor: isLocked ? 'transparent' : 'rgba(79, 172, 254, 0.1)',
                                '&:hover': {
                                  backgroundColor: isLocked ? 'transparent' : 'rgba(79, 172, 254, 0.2)',
                                }
                              }}
                            >
                              <PushPinIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Pin Instrument">
                          <IconButton
                            size="small"
                            onClick={(e) => handlePin(result, e)}
                          >
                            <PushPinIcon
                              fontSize="small"
                              sx={{
                                transform: 'rotate(45deg)',
                                color: 'text.secondary'
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </ListItem>
                );
              })
            )}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppBar;
